import moment from 'moment';
import { saveAs } from 'file-saver';

/**
 * Partially inspired from https://github.com/nwcell/ics.js
 * Thank you newcell
 */
class ICS {
  constructor(uidDomain, prodId) {
    this.uidDomain = 'rypeapp.com';
    this.prodId = 'Rype, Inc//Lesson Scheduler//EN';

    if (uidDomain) {
      this.uidDomain = uidDomain;
    }
    if (prodId) {
      this.prodId = prodId;
    }

    this.separator = navigator.appVersion.indexOf('Win') !== -1 ? '\r\n' : '\n';

    this.calendarEvents = [];

    this.calendarStart = [
      'BEGIN:VCALENDAR',
      'PRODID:-//' + this.prodId,
      'VERSION:2.0',
    ].join(this.separator);

    this.calendarEnd = this.separator + 'END:VCALENDAR';
  }

  build = () => {
    if (this.events().length < 1) {
      return false;
    }

    return this.calendar();
  }

  /**
   * Returns calendar
   * @return {string} Calendar in iCalendar format
   */
  calendar = () => {
    return (
      this.calendarStart +
      this.separator +
      this.events().join(this.separator) +
      this.calendarEnd
    );
  }

  download = (filename = 'rype_calendar_event', ext = 'ics') => {
    const calendar = this.build();

    if (!calendar) {
      return false;
    }

    const file = new File([calendar], { type: 'text/x-vCalendar;charset=utf-8' });

    saveAs(file, `${filename}.${ext}`);

    return calendar;
  }

  /**
   * Returns events array
   * @return {array} Events
   */
  events = () => {
    return this.calendarEvents;
  }

  /**
   * Add event to the calendar
   * @param  {string} booking       Booking object for event (used to generate UID)
   * @param  {string} subject       Subject/Title of event
   * @param  {string} description   Description of event
   * @param  {string} location      Location of event
   * @param  {string} beginDateTime Beginning date of event
   * @param  {string} stopDateTime  Ending date of event
   */
  addEvent({
    booking,
    summary,
    description,
    location,
    beginDateTime,
    stopDateTime,
  }) {
    // These are all required
    if (
      typeof booking === 'undefined' ||
      typeof summary === 'undefined' ||
      typeof description === 'undefined' ||
      typeof location === 'undefined' ||
      typeof beginDateTime === 'undefined' ||
      typeof stopDateTime === 'undefined'
    ) {
      return false;
    }

    const startDateTime = beginDateTime._isMoment ?
      beginDateTime :
      moment(beginDateTime);
    const endDateTime = stopDateTime._isMoment ?
      stopDateTime :
      moment(stopDateTime);
    const nowDateTime = moment();

    const uid =
      `calid-${booking.calendarId}-` +
      `sid-${booking.serviceId}-` +
      `rid-${booking.resourceId}-` +
      `cid-${booking.customerId}-` +
      `bid-${booking.id}`;

    const calendarEvent = [
      'BEGIN:VEVENT',
      this._formatLongLines(`UID:${uid}@${this.uidDomain}`),
      this._formatLongLines(`DESCRIPTION:${description}`),
      `DTSTAMP:${nowDateTime.utc().format('YYYYMMDDTHHmmss[Z]')}`,
      `DTSTART:${startDateTime.utc().format('YYYYMMDDTHHmmss[Z]')}`,
      `DTEND:${endDateTime.utc().format('YYYYMMDDTHHmmss[Z]')}`,
      this._formatLongLines(`LOCATION:${location}`),
      this._formatLongLines(`SUMMARY;LANGUAGE=en-us:${summary}`),
      'BEGIN:VALARM',
      'TRIGGER:-PT10M',
      this._formatLongLines(`DESCRIPTION:${summary}`),
      'ACTION:DISPLAY',
      'END:VALARM',
      'END:VEVENT',
    ];

    this.calendarEvents.push(calendarEvent.join(this.separator));

    return this.calendarEvents;
  }

  _formatLongLines = (line) => {
    // RFC5545 spec limits line lengths to 75 characters
    // after which a '\n\t' should be inserted
    // we will use 72 for safety
    return line
      .split(/(.{72})/)
      .filter(substr => substr !== '') // remove empty strings generated by split
      .join('\n\t'); // put it all back together
  }
}

export default ICS;



// WEBPACK FOOTER //
// ./src/utils/ics.js